<script setup>
import { ref, computed } from "vue";
import CustomModal from "../components/CustomModal.vue";

// Static payment data for HS Travel
const paiements = ref([
  {
    id: 1,
    codeConfirmation: "HS2024001",
    client: {
      nom: "Marie Dupont",
      email: "marie.dupont@email.com",
      telephone: "+216 50 123 456",
      nationalite: "France"
    },
    reservation: {
      id: 1,
      hebergement: {
        nom: "Villa Sidi Bou Said",
        type: "maison",
        ville: "Sidi Bou Said"
      },
      dates: {
        arrivee: "2024-07-15",
        depart: "2024-07-22",
        nuits: 7
      },
      prixTotal: 1680
    },
    montantPaye: 1680,
    imageRecu: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
    dateSoumission: "2024-06-02 14:30",
    statut: "En attente",
    commentaires: "Paiement effectué via virement bancaire"
  },
  {
    id: 2,
    codeConfirmation: "HS2024002",
    client: {
      nom: "Ahmed Ben Ali",
      email: "ahmed.benali@email.com",
      telephone: "+216 20 654 321",
      nationalite: "Tunisie"
    },
    reservation: {
      id: 2,
      hebergement: {
        nom: "Palmeraie Tozeur",
        type: "campement",
        ville: "Tozeur"
      },
      dates: {
        arrivee: "2024-08-10",
        depart: "2024-08-17",
        nuits: 7
      },
      prixTotal: 1050
    },
    montantPaye: 1050,
    imageRecu: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400",
    dateSoumission: "2024-05-29 16:45",
    statut: "Validé",
    commentaires: "Paiement en espèces à l'agence"
  },
  {
    id: 3,
    codeConfirmation: "HS2024003",
    client: {
      nom: "Sophie Martin",
      email: "sophie.martin@email.com",
      telephone: "+33 6 12 34 56 78",
      nationalite: "France"
    },
    reservation: {
      id: 3,
      hebergement: {
        nom: "Riad Hammamet",
        type: "maison",
        ville: "Hammamet"
      },
      dates: {
        arrivee: "2024-09-05",
        depart: "2024-09-08",
        nuits: 3
      },
      prixTotal: 285
    },
    montantPaye: 285,
    imageRecu: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
    dateSoumission: "2024-06-16 11:20",
    statut: "Rejeté",
    motifRejet: "Le montant payé ne correspond pas au montant demandé",
    commentaires: "Paiement partiel effectué"
  },
  {
    id: 4,
    codeConfirmation: "HS2024004",
    client: {
      nom: "John Smith",
      email: "john.smith@email.com",
      telephone: "+1 555 123 4567",
      nationalite: "États-Unis"
    },
    reservation: {
      id: 4,
      hebergement: {
        nom: "Dar El Medina",
        type: "maison",
        ville: "Tunis"
      },
      dates: {
        arrivee: "2024-10-20",
        depart: "2024-10-27",
        nuits: 7
      },
      prixTotal: 840
    },
    montantPaye: 840,
    imageRecu: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400",
    dateSoumission: "2024-06-11 09:15",
    statut: "En attente",
    commentaires: "Paiement via PayPal"
  },
  {
    id: 5,
    codeConfirmation: "HS2024005",
    client: {
      nom: "Fatima Al-Zahra",
      email: "fatima.alzahra@email.com",
      telephone: "+216 70 987 654",
      nationalite: "Tunisie"
    },
    reservation: {
      id: 5,
      hebergement: {
        nom: "Kasbah Tataouine",
        type: "campement",
        ville: "Tataouine"
      },
      dates: {
        arrivee: "2024-11-15",
        depart: "2024-11-20",
        nuits: 5
      },
      prixTotal: 425
    },
    montantPaye: 425,
    imageRecu: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
    dateSoumission: "2024-06-06 13:40",
    statut: "Validé",
    commentaires: "Paiement en ligne sécurisé"
  }
]);

const showModal = ref(false);
const showImageModal = ref(false);
const showStatusModal = ref(false);
const showConfirmModal = ref(false);
const showPromptModal = ref(false);
const selectedPaiement = ref(null);
const selectedImage = ref(null);
const filterStatus = ref("Tous");
const filterType = ref("Tous");

// Modal data
const modalData = ref({
  title: '',
  message: '',
  type: 'confirm',
  confirmText: 'Confirmer',
  cancelText: 'Annuler',
  inputLabel: '',
  inputPlaceholder: '',
  inputValue: '',
  selectLabel: '',
  selectOptions: [],
  selectedValue: '',
  onConfirm: null,
  onCancel: null
});

// Computed properties for statistics
const stats = computed(() => {
  const total = paiements.value.length;
  const enAttente = paiements.value.filter(p => p.statut === "En attente").length;
  const valides = paiements.value.filter(p => p.statut === "Validé").length;
  const rejetes = paiements.value.filter(p => p.statut === "Rejeté").length;
  const montantTotal = paiements.value
    .filter(p => p.statut === "Validé")
    .reduce((sum, p) => sum + p.montantPaye, 0);

  return { total, enAttente, valides, rejetes, montantTotal };
});

// Filtered payments
const filteredPaiements = computed(() => {
  let filtered = paiements.value;

  // Filter by status
  if (filterStatus.value !== "Tous") {
    filtered = filtered.filter(p => p.statut === filterStatus.value);
  }

  // Filter by accommodation type
  if (filterType.value !== "Tous") {
    filtered = filtered.filter(p => p.reservation.hebergement.type === filterType.value);
  }

  return filtered;
});

// Functions
const openModal = (paiement) => {
  selectedPaiement.value = paiement;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedPaiement.value = null;
};

const openImageModal = (imageUrl) => {
  selectedImage.value = imageUrl;
  showImageModal.value = true;
};

const closeImageModal = () => {
  showImageModal.value = false;
  selectedImage.value = null;
};

const validerPaiement = (paiement) => {
  modalData.value = {
    title: 'Valider le paiement',
    message: `Confirmer le paiement de ${paiement.montantPaye} DT pour ${paiement.client.nom} ?`,
    type: 'confirm',
    confirmText: 'Valider',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-success',
    onConfirm: () => {
      paiement.statut = "Validé";
      // Supprimer le motif de rejet s'il existe
      delete paiement.motifRejet;
      showConfirmModal.value = false;
      // Ici on pourrait envoyer une notification au client
      alert(`Paiement validé ! Un email de confirmation a été envoyé à ${paiement.client.email}`);
    },
    onCancel: () => {
      showConfirmModal.value = false;
    }
  };
  showConfirmModal.value = true;
};

const rejeterPaiement = (paiement) => {
  modalData.value = {
    title: 'Rejeter le paiement',
    message: `Motif du rejet pour le paiement de ${paiement.client.nom} :`,
    type: 'prompt',
    confirmText: 'Rejeter',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-danger',
    inputLabel: 'Motif du rejet',
    inputPlaceholder: 'Document non conforme, etc.',
    inputValue: '',
    onConfirm: (motif) => {
      if (motif) {
        paiement.statut = "Rejeté";
        paiement.motifRejet = motif;
        // Ici on pourrait envoyer une notification au client
        alert(`Paiement rejeté. Un email d'information a été envoyé à ${paiement.client.email}`);
      }
      showPromptModal.value = false;
    },
    onCancel: () => {
      showPromptModal.value = false;
    }
  };
  showPromptModal.value = true;
};

const getStatusColor = (status) => {
  switch (status) {
    case "Validé": return "success";
    case "En attente": return "warning";
    case "Rejeté": return "danger";
    default: return "secondary";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "Validé": return "fa-check-circle";
    case "En attente": return "fa-clock";
    case "Rejeté": return "fa-times-circle";
    default: return "fa-question-circle";
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case "maison": return "fa-home";
    case "campement": return "fa-campground";
    default: return "fa-question-circle";
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case "maison": return "Maison d'hôte";
    case "campement": return "Campement";
    default: return type;
  }
};

const modifierStatutPaiement = (paiement) => {
  const statutActuel = paiement.statut;
  const nouveauxStatuts = ["En attente", "Validé", "Rejeté"];
  const autresStatuts = nouveauxStatuts.filter(s => s !== statutActuel);

  modalData.value = {
    title: 'Modifier le statut',
    message: `Changer le statut de "${statutActuel}" vers :`,
    type: 'select',
    confirmText: 'Modifier',
    cancelText: 'Annuler',
    confirmButtonClass: 'btn-warning',
    selectLabel: 'Nouveau statut',
    selectOptions: autresStatuts.map(statut => ({ value: statut, label: statut })),
    selectedValue: autresStatuts[0],
    onConfirm: (nouveauStatut) => {
      if (nouveauStatut && nouveauStatut !== statutActuel) {
        // Confirmation finale
        modalData.value = {
          title: 'Confirmer le changement',
          message: `Confirmer le changement de statut de "${statutActuel}" vers "${nouveauStatut}" pour le paiement de ${paiement.client.nom} ?`,
          type: 'confirm',
          confirmText: 'Confirmer',
          cancelText: 'Annuler',
          onConfirm: () => {
            paiement.statut = nouveauStatut;

            // Pour les paiements rejetés, demander un motif
            if (nouveauStatut === "Rejeté") {
              modalData.value = {
                title: 'Motif du rejet',
                message: 'Veuillez indiquer le motif du rejet :',
                type: 'prompt',
                confirmText: 'Rejeter',
                cancelText: 'Annuler',
                confirmButtonClass: 'btn-danger',
                inputLabel: 'Motif du rejet',
                inputPlaceholder: 'Document non conforme, etc.',
                inputValue: '',
                onConfirm: (motif) => {
                  if (motif) {
                    paiement.motifRejet = motif;
                  }
                  showPromptModal.value = false;
                },
                onCancel: () => {
                  showPromptModal.value = false;
                }
              };
              showPromptModal.value = true;
              return;
            } else if (nouveauStatut === "Validé") {
              // Supprimer le motif de rejet s'il existe
              delete paiement.motifRejet;
            }

            showConfirmModal.value = false;
          },
          onCancel: () => {
            showConfirmModal.value = false;
          }
        };
        showConfirmModal.value = true;
      }
      showStatusModal.value = false;
    },
    onCancel: () => {
      showStatusModal.value = false;
    }
  };
  showStatusModal.value = true;
};
</script>

<template>
  <div class="py-4 container-fluid">
    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">Total Paiements</p>
                  <h5 class="font-weight-bolder">{{ stats.total }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-primary shadow-primary text-center border-radius-md">
                  <i class="ni ni-credit-card text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">En Attente</p>
                  <h5 class="font-weight-bolder">{{ stats.enAttente }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-warning shadow-warning text-center border-radius-md">
                  <i class="ni ni-time-alarm text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">Validés</p>
                  <h5 class="font-weight-bolder">{{ stats.valides }}</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-success shadow-success text-center border-radius-md">
                  <i class="ni ni-check-bold text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-uppercase font-weight-bold">Montant Total</p>
                  <h5 class="font-weight-bolder">{{ stats.montantTotal }} DT</h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div class="icon icon-shape bg-gradient-info shadow-info text-center border-radius-md">
                  <i class="ni ni-money-coins text-lg opacity-10" aria-hidden="true"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters and Actions -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <h6>Gestion des Paiements</h6>
              <div class="d-flex align-items-center flex-wrap gap-3">
                <div class="d-flex align-items-center">
                  <label class="form-label me-2 mb-0">Type d'hébergement:</label>
                  <select v-model="filterType" class="form-select form-select-sm" style="width: auto;">
                    <option value="Tous">Tous</option>
                    <option value="maison">Maisons d'hôte</option>
                    <option value="campement">Campements</option>
                  </select>
                </div>
                <div class="d-flex align-items-center">
                  <label class="form-label me-2 mb-0">Statut:</label>
                  <select v-model="filterStatus" class="form-select form-select-sm" style="width: auto;">
                    <option value="Tous">Tous</option>
                    <option value="En attente">En attente</option>
                    <option value="Validé">Validés</option>
                    <option value="Rejeté">Rejetés</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payments Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Client
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Hébergement
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Type
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Code Confirmation
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Statut
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Montant
                    </th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Reçu
                    </th>
                    <th class="text-secondary opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="paiement in filteredPaiements" :key="paiement.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ paiement.client.nom }}</h6>
                          <p class="text-xs text-secondary mb-0">
                            {{ paiement.client.email }}
                          </p>
                          <small class="text-muted">{{ paiement.client.nationalite }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ paiement.reservation.hebergement.nom }}</p>
                      <p class="text-xs text-secondary mb-0">{{ paiement.reservation.hebergement.ville }}</p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm bg-gradient-info">
                        <i :class="`fa ${getTypeIcon(paiement.reservation.hebergement.type)} me-1`"></i>
                        {{ getTypeLabel(paiement.reservation.hebergement.type) }}
                      </span>
                    </td>
                    <td>
                      <span class="badge badge-sm bg-gradient-primary">{{ paiement.codeConfirmation }}</span>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span
                        class="badge badge-sm"
                        :class="`bg-gradient-${getStatusColor(paiement.statut)}`"
                      >
                        <i :class="`fa ${getStatusIcon(paiement.statut)} me-1`"></i>
                        {{ paiement.statut }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-secondary text-xs font-weight-bold">
                        DT{{ paiement.montantPaye }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <button
                        class="btn btn-link text-info mb-0"
                        @click="openImageModal(paiement.imageRecu)"
                        title="Voir le reçu de paiement"
                      >
                        <i class="fa fa-image text-xs"></i>
                      </button>
                    </td>
                    <td class="align-middle">
                      <button
                        class="btn btn-link text-info mb-0 me-2"
                        @click="openModal(paiement)"
                        title="Voir détails"
                      >
                        <i class="fa fa-eye text-xs"></i>
                      </button>
                      <button
                        class="btn btn-link text-warning mb-0 me-2"
                        @click="modifierStatutPaiement(paiement)"
                        title="Modifier statut"
                      >
                        <i class="fa fa-edit text-xs"></i>
                      </button>
                      <button
                        v-if="paiement.statut === 'En attente'"
                        class="btn btn-link text-success mb-0 me-2"
                        @click="validerPaiement(paiement)"
                        title="Valider le paiement"
                      >
                        <i class="fa fa-check text-xs"></i>
                      </button>
                      <button
                        v-if="paiement.statut === 'En attente'"
                        class="btn btn-link text-danger mb-0"
                        @click="rejeterPaiement(paiement)"
                        title="Rejeter le paiement"
                      >
                        <i class="fa fa-times text-xs"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Details Modal -->
    <div v-if="showModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Détails du Paiement #{{ selectedPaiement?.id }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div v-if="selectedPaiement" class="modal-body">
            <div class="row">
              <div class="col-md-6">
                <h6>Informations Client</h6>
                <div class="card p-3 mb-3">
                  <p><strong>Nom:</strong> {{ selectedPaiement.client.nom }}</p>
                  <p><strong>Email:</strong> {{ selectedPaiement.client.email }}</p>
                  <p><strong>Téléphone:</strong> {{ selectedPaiement.client.telephone }}</p>
                  <p><strong>Nationalité:</strong> {{ selectedPaiement.client.nationalite }}</p>
                </div>

                <h6>Détails de la Réservation</h6>
                <div class="card p-3">
                  <p><strong>Hébergement:</strong> {{ selectedPaiement.reservation.hebergement.nom }}</p>
                  <p><strong>Type:</strong>
                    <span class="badge bg-gradient-info ms-2">
                      <i :class="`fa ${getTypeIcon(selectedPaiement.reservation.hebergement.type)} me-1`"></i>
                      {{ getTypeLabel(selectedPaiement.reservation.hebergement.type) }}
                    </span>
                  </p>
                  <p><strong>Ville:</strong> {{ selectedPaiement.reservation.hebergement.ville }}</p>
                  <p><strong>Arrivée:</strong> {{ selectedPaiement.reservation.dates.arrivee }}</p>
                  <p><strong>Départ:</strong> {{ selectedPaiement.reservation.dates.depart }}</p>
                  <p><strong>Prix total:</strong> {{ selectedPaiement.reservation.prixTotal }} DT</p>
                </div>
              </div>
              <div class="col-md-6">
                <h6>Informations de Paiement</h6>
                <div class="card p-3 mb-3">
                  <p><strong>Code de confirmation:</strong>
                    <span class="badge bg-gradient-primary ms-2">{{ selectedPaiement.codeConfirmation }}</span>
                  </p>
                  <p><strong>Montant payé:</strong> {{ selectedPaiement.montantPaye }} DT</p>
                  <p><strong>Statut:</strong>
                    <span
                      class="badge ms-2"
                      :class="`bg-gradient-${getStatusColor(selectedPaiement.statut)}`"
                    >
                      {{ selectedPaiement.statut }}
                    </span>
                  </p>
                  <p><strong>Date de soumission:</strong> {{ selectedPaiement.dateSoumission }}</p>
                  <p v-if="selectedPaiement.motifRejet">
                    <strong>Motif de rejet:</strong> {{ selectedPaiement.motifRejet }}
                  </p>
                </div>

                <h6>Commentaires</h6>
                <div class="card p-3">
                  <p>{{ selectedPaiement.commentaires }}</p>
                </div>

                <h6>Reçu de Paiement</h6>
                <div class="card p-3">
                  <img
                    :src="selectedPaiement.imageRecu"
                    class="img-fluid rounded cursor-pointer"
                    @click="openImageModal(selectedPaiement.imageRecu)"
                    style="max-height: 200px; object-fit: cover; cursor: pointer;"
                    alt="Reçu de paiement"
                  />
                  <small class="text-muted mt-2 d-block">Cliquez sur l'image pour l'agrandir</small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.8);">
      <div class="modal-dialog modal-xl">
        <div class="modal-content bg-transparent border-0">
          <div class="modal-body text-center p-0">
            <img
              :src="selectedImage"
              class="img-fluid rounded"
              alt="Reçu de paiement agrandi"
            />
          </div>
          <div class="modal-footer bg-transparent border-0">
            <button type="button" class="btn btn-light" @click="closeImageModal">
              <i class="fa fa-times me-2"></i>Fermer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Modals -->
    <CustomModal
      v-model:show="showConfirmModal"
      :title="modalData.title"
      :message="modalData.message"
      :type="modalData.type"
      :confirm-text="modalData.confirmText"
      :cancel-text="modalData.cancelText"
      :confirm-button-class="modalData.confirmButtonClass"
      :input-label="modalData.inputLabel"
      :input-placeholder="modalData.inputPlaceholder"
      :input-value="modalData.inputValue"
      :select-label="modalData.selectLabel"
      :select-options="modalData.selectOptions"
      :selected-value="modalData.selectedValue"
      @confirm="modalData.onConfirm"
      @cancel="modalData.onCancel"
    />

    <CustomModal
      v-model:show="showPromptModal"
      :title="modalData.title"
      :message="modalData.message"
      :type="modalData.type"
      :confirm-text="modalData.confirmText"
      :cancel-text="modalData.cancelText"
      :confirm-button-class="modalData.confirmButtonClass"
      :input-label="modalData.inputLabel"
      :input-placeholder="modalData.inputPlaceholder"
      :input-value="modalData.inputValue"
      @confirm="modalData.onConfirm"
      @cancel="modalData.onCancel"
    />

    <CustomModal
      v-model:show="showStatusModal"
      :title="modalData.title"
      :message="modalData.message"
      :type="modalData.type"
      :confirm-text="modalData.confirmText"
      :cancel-text="modalData.cancelText"
      :confirm-button-class="modalData.confirmButtonClass"
      :select-label="modalData.selectLabel"
      :select-options="modalData.selectOptions"
      :selected-value="modalData.selectedValue"
      @confirm="modalData.onConfirm"
      @cancel="modalData.onCancel"
    />

  </div>
</template>
